<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :columns="columnDefs"
      :settings="{ hideTable: true, hideExport: true, customRowsKey: 'transactions', autoSelectLocation: true, title: title }"
      :endpoint="`accounting/accounts/${accountId}/log`"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter>
        <!-- Add any filters here if needed -->
      </template>

      <template #cards="{ data }">
        <!-- Normal Balance (above the card) -->
        <div class="normal-balance">
          {{ t('normalBalance') }}:
          <strong>{{ isDebitNormal ? t('debit') : t('credit') }}</strong>
        </div>

        <div class="account-log-container">
          <!-- Custom Table -->
          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>{{ t('id') }}</th>
                  <th>{{ t('dateTime') }}</th>
                  <th class="text-right">{{ t('credit') }}</th>
                  <th class="text-right">{{ t('debit') }}</th>
                  <th>{{ t('from') }}</th>
                  <th>{{ t('to') }}</th>
                  <th>{{ t('employee') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="transaction in transactions" :key="transaction.id">
                  <td>{{ transaction.externalID }}</td>
                  <td class="whitespace-nowrap">{{ formatPostgresDate(transaction.createdAt, 'DD-MM-YYYY hh:mm A') }}</td>
                  <td class="text-right credit-amount">
                    {{ formatAmount(transaction.creditAmount, transaction.currency, transaction.precision) }}
                  </td>
                  <td class="text-right debit-amount">
                    {{ formatAmount(transaction.debitAmount, transaction.currency, transaction.precision) }}
                  </td>
                  <td>{{ getFromAccount(transaction) }}</td>
                  <td>{{ getToAccount(transaction) }}</td>
                  <td>{{ transaction.user?.name || '-' }}</td>
                </tr>
              </tbody>
              <tfoot v-if="transactions.length > 0">
                <tr class="totals-row">
                  <td colspan="2"></td>
                  <td class="text-right credit-total">
                    {{ formatAmount(totalCredit, transactions[0]?.currency || 'IQD', transactions[0]?.precision || 0) }}
                  </td>
                  <td class="text-right debit-total">
                    {{ formatAmount(totalDebit, transactions[0]?.currency || 'IQD', transactions[0]?.precision || 0) }}
                  </td>
                  <td colspan="3"></td>
                </tr>
                <tr class="balance-row">
                  <td colspan="2" class="balance-label text-right">{{ t('balance') }}</td>
                  <td colspan="2" class="balance-amount">{{ formattedBalance() }}</td>
                  <td colspan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useRoute } from 'vue-router'
import { useContext } from '@/plugins/context'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatMoney } from '@/utils/money'
import { formatPostgresDate } from '@/utils/date'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      accountLog: 'Account Log',
      id: 'ID',
      dateTime: 'Date & Time',
      credit: 'Credit',
      debit: 'Debit',
      from: 'From',
      to: 'To',
      employee: 'Employee',
      normalBalance: 'Normal Balance',
      balance: 'Balance'
    },
    ar: {
      accountLog: 'سجل الحساب',
      id: 'رقم القيد',
      dateTime: 'التاريخ والوقت',
      credit: 'دائن',
      debit: 'مدين',
      from: 'من',
      to: 'إلى',
      employee: 'الموظف',
      normalBalance: 'الميزان الطبيعي',
      balance: 'الرصيد'
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])
const route = useRoute()
const accountId = ref<string>(route.params.id as string)
const ctx = useContext()
const transactions = ref<any[]>([])
const totalCredit = ref<number>(0)
const totalDebit = ref<number>(0)
const title = ref<string>('')
// The account's normal balance ("debit" | "credit"), straight from account.balance.
const normalBalance = ref<string>('debit')
const isDebitNormal = computed(() => normalBalance.value !== 'credit')

// Balance: debit-normal => Debit - Credit; credit-normal => Credit - Debit.
const balance = computed(() =>
  isDebitNormal.value ? totalDebit.value - totalCredit.value : totalCredit.value - totalDebit.value
)

onBeforeMount(async () => {
  columnDefs.value = []
})

function onDataLoaded(data: any) {
  if (data && data.account) {
    title.value = ctx.locale == 'en' ? data.account.enName : data.account.arName
    normalBalance.value = (data.account.balance || 'debit').toLowerCase()
  }

  if (data && data.transactions) {
    transactions.value = data.transactions

    // Calculate totals
    totalCredit.value = transactions.value.reduce((sum, t) => sum + (t.creditAmount || 0), 0)
    totalDebit.value = transactions.value.reduce((sum, t) => sum + (t.debitAmount || 0), 0)
  }
}

function currentCurrency(): string {
  return transactions.value[0]?.currency || 'IQD'
}

// Balance shown with its currency code (e.g. "IQD 70,000").
function formattedBalance(): string {
  return formatMoney(balance.value, currentCurrency())
}

function formatAmount(amount: number, currency: string, precision: number): string {
  if (!amount || amount === 0) return ''
  return formatMoney(amount, currency).replace(currency, '').trim()
}

function getFromAccount(transaction: any): string {
  if (!transaction.creditedAccount) return '-'

  const account = transaction.creditedAccount
  const name = ctx.locale === 'en' ? account.enName : account.arName

  return name || '-'
}

function getToAccount(transaction: any): string {
  if (!transaction.debitedAccount) return '-'

  const account = transaction.debitedAccount
  const name = ctx.locale === 'en' ? account.enName : account.arName

  return name || '-'
}
</script>

<style scoped>
.account-log-container {
  width: 100%;
  background-color: #ffffff;
  border-radius: 4px;
  overflow: hidden;
}

.normal-balance {
  text-align: end;
  padding: 12px 16px;
  margin-bottom: 4px;
  font-size: 15px;
  color: #1e3a5f;
}

.normal-balance strong {
  color: #1e3a5f;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.custom-table thead {
  background-color: #f9fafb;
}

.custom-table thead tr {
  border-bottom: 1px solid #e5e7eb;
}

.custom-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-right: 1px solid #e5e7eb;
}

.custom-table th:last-child {
  border-right: none;
}

.custom-table th.text-right {
  text-align: right;
}

.custom-table tbody tr {
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.2s;
}

.custom-table tbody tr:hover {
  background-color: #f9fafb;
}

.custom-table tbody tr:last-child {
  border-bottom: none;
}

.custom-table td {
  padding: 12px 16px;
  color: #1f2937;
  border-right: 1px solid #f3f4f6;
}

.custom-table td:last-child {
  border-right: none;
}

.custom-table td.text-right {
  text-align: right;
}

.custom-table td.credit-amount {
  color: #2563eb;
  font-weight: 500;
}

.custom-table td.debit-amount {
  color: #1f2937;
  font-weight: 500;
}

.custom-table tfoot {
  background-color: #f3f4f6;
  font-weight: 600;
}

.custom-table tfoot tr.totals-row {
  border-top: 2px solid #d1d5db;
}

.custom-table tfoot td {
  padding: 14px 16px;
  font-size: 14px;
}

.custom-table tfoot td.credit-total {
  color: #2563eb;
  font-weight: 700;
}

.custom-table tfoot td.debit-total {
  color: #1f2937;
  font-weight: 700;
}

.custom-table tfoot tr.balance-row {
  background-color: #dbeafe;
  border-top: 1px solid #bfdbfe;
}

.custom-table tfoot td.balance-label {
  color: #1f2937;
  font-weight: 700;
}

.custom-table tfoot td.balance-amount {
  text-align: center;
  color: #1e3a5f;
  font-weight: 700;
  font-size: 16px;
}

/* RTL Support */
:deep(.el-select) {
  width: 100%;
}

.whitespace-nowrap {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .custom-table {
    font-size: 12px;
  }

  .custom-table th,
  .custom-table td {
    padding: 8px 12px;
  }
}
</style>
